import { useEffect, useState } from 'react'
import { Trash2, CheckSquare, Square, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { Message } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function FolderPage({ folder, title }: { folder: string; title: string }) {
  const { t } = useI18n()
  const [messages, setMessages] = useState<Message[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')

  const load = () => api.get<Message[]>(`/messages?folder=${folder}`).then((data) => { setMessages(data); setSelected(new Set()) }).catch((e: Error) => setError(e.message))
  useEffect(() => { load() }, [folder])

  function toggleSelect(id: string) {
    setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleAll() {
    if (selected.size === messages.length) setSelected(new Set())
    else setSelected(new Set(messages.map((m) => m.id)))
  }
  async function deleteSelected() {
    if (selected.size === 0) return
    if (!confirm(`${t('common.delete')} ${selected.size}?`)) return
    try { await api.post('/messages/batch-delete', { ids: [...selected] }); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }
  async function deleteOne(id: string) {
    try { await api.delete(`/messages/${id}`); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }
  async function emptyTrash() {
    if (!confirm(t('common.delete') + ' all?')) return
    try { await api.post('/messages/empty-trash'); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  return <>
    <PageHeader title={title} action={
      <div className="flex items-center gap-2">
        {selected.size > 0 && <button onClick={deleteSelected} className="flex h-10 items-center gap-2 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground"><Trash2 size={16}/>{selected.size}</button>}
        {folder === 'trash' && messages.length > 0 && <button onClick={emptyTrash} className="flex h-10 items-center gap-2 rounded-md border bg-card px-4 text-sm font-medium text-destructive"><XCircle size={16}/>{t('common.delete')}</button>}
      </div>
    } />
    {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
    {messages.length === 0 ? <EmptyState title="user.emptyMailbox" /> : (
      <div className="panel divide-y overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/20">
          <button onClick={toggleAll}>{selected.size === messages.length ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
          <span className="text-xs text-muted-foreground">{messages.length}</span>
        </div>
        {messages.map((message) => (
          <article key={message.id} className="flex items-start gap-3 p-4 transition-colors hover:bg-muted/30">
            <button onClick={() => toggleSelect(message.id)} className="mt-1 shrink-0">{selected.has(message.id) ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{message.sender}</p>
              <p className="mt-1 text-sm">{message.subject || '(No subject)'}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">{message.body}</p>
            </div>
            <button onClick={() => deleteOne(message.id)} className="shrink-0 rounded border p-1.5 text-destructive hover:bg-destructive/10"><Trash2 size={14}/></button>
          </article>
        ))}
      </div>
    )}
  </>
}
